import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  buildInitialOwnerShares,
  OwnerShare,
} from '@app/components/pr-form/pr-input-owner-share/pr-input-owner-share.component';
import { UtilsService } from '@app/providers/utils/utils.service';
import { EaEstateLoanFormService } from '../ea-estate-loan-form/ea-estate-loan-form.service';
import { EaEstimationFormService } from '../ea-estimation-form/ea-estimation-form.service';
import { EaFeesFormService } from '../ea-fees-form/ea-fees-form.service';
import { EaRealestateFormService } from '../ea-realestate-form/ea-realestate-form.service';
import { EaRentFormService } from '../ea-rent-form/ea-rent-form.service';

@Injectable({
  providedIn: 'root',
})
export class EaLmnpLmpFormService {
  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected utilsService: UtilsService,
    protected eaEstimationFormService: EaEstimationFormService,
    protected eaRealestateFormService: EaRealestateFormService,
    protected eaEstateLoanFormService: EaEstateLoanFormService,
    protected eaRentFormService: EaRentFormService,
    protected eaFeesFormService: EaFeesFormService,
  ) {}

  public generate(entity: any) {
    let options: AbstractControlOptions = {};
    if (!entity) entity = {};

    let fiscalLaw: any = {};
    if (entity.fiscalLaw) fiscalLaw = entity.fiscalLaw;

    let mainLoan: any = undefined;
    if (entity.loans && entity.loans[0]) mainLoan = entity.loans[0];

    let mainAddress: any = {};
    if (entity.property) mainAddress = entity.property;
    return this.formBuilder.group(
      {
        id: [entity ? entity.id : null, Validators.nullValidator],
        type: [entity ? entity.type : null, Validators.required],
        userId: [entity ? entity.userId : null, Validators.nullValidator],
        accountId: [entity ? entity.accountId : null, Validators.nullValidator],
        typeUrl: [entity ? entity.typeUrl : null, Validators.nullValidator],
        // Détenteurs + part de détention (`OwnerShare[]`) pour
        // `<pr-input-owner-share>`. `share` repris s'il existe, sinon 1/n.
        owners: [buildInitialOwnerShares(entity), Validators.required],
        subscriptionSource: [
          entity ? entity.subscriptionSource : null,
          Validators.required,
        ],
        subscriptionSourceDetail: [
          entity ? entity.subscriptionSourceDetail : null,
          Validators.nullValidator,
        ],
        residenceType: [
          entity && mainAddress?.type ? mainAddress.type : 'APPARTMENT',
          Validators.required,
        ],
        fiscalLaw: this.formBuilder.group({
          type: [
            fiscalLaw && fiscalLaw.type ? fiscalLaw.type : null,
            Validators.nullValidator,
          ],
          startDate: [
            fiscalLaw && fiscalLaw.startDate ? fiscalLaw.startDate : null,
            Validators.nullValidator,
          ],
          duration: [
            fiscalLaw && fiscalLaw.duration ? fiscalLaw.duration : null,
            Validators.nullValidator,
          ],
          subscriptionPartner: [
            entity ? entity.subscriptionPartner : null,
            Validators.nullValidator,
          ],
        }),
        loans: this.eaEstateLoanFormService.generate(
          mainLoan || { accountId: entity.accountId },
        ),
        realestate: this.eaRealestateFormService.generate(entity),
        fees: this.eaFeesFormService.generate(entity),
        rents: this.eaRentFormService.generate(entity),
        estimations: this.eaEstimationFormService.generate(entity),
      },
      options,
    );
  }

  public extract(form: UntypedFormGroup, nestedForms?) {
    let realestate = form.get('realestate').value;

    let dto = {
      typeUrl: this.utilsService.emptyIsNull(form.value.typeUrl),
      type: this.utilsService.emptyIsNull(form.value.type),
      id: this.utilsService.emptyIsNull(form.value.id),
      accountId: this.utilsService.emptyIsNull(form.value.accountId),
      label: this.utilsService.emptyIsNull(realestate.label),
      purchasePrice: this.utilsService.emptyIsNull(realestate.purchasePrice),
      startDate: this.utilsService.toIsoDate(realestate.startDate),
      subscriptionSource: this.utilsService.emptyIsNull(
        form.value.subscriptionSource,
      ),
      subscriptionPartner: this.utilsService.emptyIsNull(
        form.value.fiscalLaw?.subscriptionPartner?.id,
      ),
      subscriptionSourceDetail: this.utilsService.emptyIsNull(
        form.value.subscriptionSourceDetail,
      ),
      // Détenteurs + part de détention (`share`, fraction 0..1) saisis via
      // `<pr-input-owner-share>` : la valeur est déjà un `OwnerShare[]`.
      owners: ((form.value.owners || []) as OwnerShare[])
        .filter(o => o?.ownerId != null)
        .map(o => ({ ownerId: Number(o.ownerId), share: o.share })),
      property: {
        type: this.utilsService.emptyIsNull(realestate.residenceType),
        areaSize: this.utilsService.emptyIsNull(realestate.areaSize),
        street: this.utilsService.emptyIsNull(realestate.address.street),
        zipCode: this.utilsService.emptyIsNull(realestate.address.zipCode),
        city: this.utilsService.emptyIsNull(realestate.address.city),
        country: this.utilsService.emptyIsNull(
          realestate.address.country.value
            ? realestate.address.country.value
            : realestate.address.country,
        ),
      },
      fiscalLaw: form.get('fiscalLaw').dirty
        ? {
            current: true,
            type: this.utilsService.emptyIsNull(form.value.fiscalLaw.type.key),
            startDate: this.utilsService.toIsoDate(
              form.value.fiscalLaw.startDate,
            ),
            duration: this.utilsService.emptyIsNull(
              form.value.fiscalLaw.duration,
            ),
          }
        : null,
      expenses: form.get('fees').value.fees,
      rents: form.get('rents').value.rents,
      estimations: form.get('estimations').value.array,
      loans: this.eaEstateLoanFormService.extract(
        form.get('loans') as UntypedFormGroup,
      ),
    };
    return dto;
  }
}
